import { buildSettingsCatalog, CHANNEL_SETTINGS } from '@nexus/core';
import { Badge, Button, SettingsRow, StatusBadge, toast } from '@nexus/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdminError } from '../admin/shared';
import { api } from '../api/client';
import { formatDate } from '../i18n/format';
import { personalChannelsQuery, type PersonalChannels } from './channels-api';
import { currentEndpoint, dismissPrompt, pushBlocked, pushSupport, unsubscribePush } from './push';
import { usePushSwitch } from './push-switch';
import { PersonalSettingRow } from './setting-rows';

const kernelSetting = (key: string) =>
  buildSettingsCatalog([]).find((setting) => setting.key === key)!;

/** Settings the channel rows below show; the profile leaves them out of its generic list. */
export const CHANNEL_SETTING_KEYS: readonly string[] = Object.values(CHANNEL_SETTINGS);

/**
 * Rows of the profile section "Benachrichtigungen" for the channels the company set up:
 * e-mail and Teams switch with a personal setting, push per device.
 */
export function NotificationChannelRows() {
  const { t } = useTranslation();
  const channels = useQuery(personalChannelsQuery);
  const data = channels.data;
  if (!data) return null;
  const none = !data.email.available && !data.teams.available && !data.push.available;
  return (
    <>
      <SettingsRow
        label={t('profile.channels.app.label')}
        description={
          none ? t('profile.channels.app.onlyChannel') : t('profile.channels.app.description')
        }
      >
        <StatusBadge status="success">{t('profile.channels.always')}</StatusBadge>
      </SettingsRow>
      {data.email.available && (
        <PersonalSettingRow
          setting={kernelSetting(CHANNEL_SETTINGS.email)}
          description={
            data.email.address
              ? t('profile.channels.email.description', { address: data.email.address })
              : t('profile.channels.email.noAddress')
          }
        />
      )}
      {data.teams.available && (
        <PersonalSettingRow setting={kernelSetting(CHANNEL_SETTINGS.teams)} />
      )}
      {data.push.available && data.push.publicKey && (
        <PushRows push={data.push} publicKey={data.push.publicKey} />
      )}
    </>
  );
}

/**
 * Push for this device and the other devices of the person (ADR-0058). On iPhone and iPad only
 * the app on the home screen can receive push, so the row explains how to get there.
 */
function PushRows({ push, publicKey }: { push: PersonalChannels['push']; publicKey: string }) {
  const { t, i18n } = useTranslation();
  const client = useQueryClient();
  const reportError = useAdminError();
  const [endpoint, setEndpoint] = useState<string | null | undefined>(undefined);
  const { switchOn, switching, known } = usePushSwitch();
  const support = pushSupport();
  const refresh = () => client.invalidateQueries(personalChannelsQuery);

  useEffect(() => {
    let current = true;
    void currentEndpoint()
      .catch(() => null)
      .then((value) => current && setEndpoint(value));
    return () => {
      current = false;
    };
  }, [push.devices]);

  const here = push.devices.find((device) => endpoint && device.endpoint === endpoint);
  const others = push.devices.filter((device) => device !== here);

  const switchOff = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/notifications/channels/push/${id}`);
      if (id === here?.id) {
        // Switched off here on purpose: the question after the installation stays away.
        dismissPrompt();
        await unsubscribePush();
      }
    },
    onSuccess: async () => {
      toast.success(t('profile.channels.push.switchedOff'));
      await refresh();
    },
    onError: (error) => reportError(error),
  });

  const test = useMutation({
    mutationFn: () => api.post('/notifications/channels/push/test'),
    onSuccess: () => toast.success(t('profile.channels.push.testSent')),
    onError: (error) => reportError(error, known),
  });

  let description: string;
  if (here) description = t('profile.channels.push.active');
  else if (support === 'install') description = t('profile.channels.push.install');
  else if (support === 'unsupported') description = t('profile.channels.push.unsupported');
  else if (pushBlocked()) description = t('profile.channels.push.blocked');
  else description = t('profile.channels.push.description');

  return (
    <>
      <SettingsRow
        label={t('profile.channels.push.label')}
        description={description}
        badge={here ? <Badge>{t('profile.channels.push.on')}</Badge> : null}
      >
        {here ? (
          <>
            <Button icon="bell" loading={test.isPending} onClick={() => test.mutate()}>
              {t('profile.channels.push.test')}
            </Button>
            <Button
              variant="ghost"
              icon="bell-off"
              loading={switchOff.isPending && switchOff.variables === here.id}
              onClick={() => switchOff.mutate(here.id)}
            >
              {t('profile.channels.push.switchOff')}
            </Button>
          </>
        ) : (
          support === 'supported' &&
          !pushBlocked() &&
          endpoint !== undefined && (
            <Button icon="bell" loading={switching} onClick={() => void switchOn(publicKey)}>
              {t('profile.channels.push.switchOn')}
            </Button>
          )
        )}
      </SettingsRow>
      {others.map((device) => (
        <SettingsRow
          key={device.id}
          label={t(`profile.channels.push.devices.${device.device}`)}
          description={t('profile.channels.push.since', {
            date: formatDate(new Date(device.createdAt), i18n.language),
          })}
        >
          <Button
            variant="ghost"
            icon="bell-off"
            loading={switchOff.isPending && switchOff.variables === device.id}
            onClick={() => switchOff.mutate(device.id)}
          >
            {t('profile.channels.push.switchOff')}
          </Button>
        </SettingsRow>
      ))}
    </>
  );
}
